import type { Href } from 'expo-router';

import type { Locale } from '@todo/core/i18n/locales';
import { SITE, type SiteSection } from '@/site';

/**
 * Site-header navigation, shared by the native and web header variants.
 *
 * Items with a `pathname` render as real expo-router <Link>s — i.e. actual
 * `<a href>` elements on web. That matters beyond tidiness: the header is the
 * only navigation present on every page, and before this the whole site
 * shipped zero anchors, so nothing linked anywhere in the static HTML.
 *
 * `key` indexes into the `nav.*` i18n namespace.
 */
export type NavPathname =
  | '/[lang]'
  | '/[lang]/events'
  | '/[lang]/tours'
  | '/[lang]/wine-tours'
  | '/[lang]/boats'
  | '/[lang]/hotels'
  | '/[lang]/articles'
  | '/[lang]/cities';

export type NavLink = { key: string; pathname: NavPathname };

const SECTION_PATHNAMES: Record<SiteSection, NavPathname> = {
  events: '/[lang]/events',
  tours: '/[lang]/tours',
  wineTours: '/[lang]/wine-tours',
  boats: '/[lang]/boats',
  hotels: '/[lang]/hotels',
  articles: '/[lang]/articles',
  cities: '/[lang]/cities',
};

/** The site's sections (SITE.sections), in header order. */
export const navLinks: NavLink[] = SITE.sections.map((key) => ({ key, pathname: SECTION_PATHNAMES[key] }));

/**
 * Href for a nav destination under the active locale.
 *
 * The `as Href` is needed because typed routes resolve `params` from a *single*
 * literal pathname, and `NavPathname` is a union — TypeScript can't narrow
 * `link.pathname` back to one route when mapping over `navLinks`. Every member
 * of the union is a real route taking exactly one `lang` param, so the cast is
 * safe; keeping it here means the two header variants stay cast-free.
 */
export function navHref(pathname: NavPathname, locale: Locale): Href {
  return { pathname, params: { lang: locale } } as Href;
}
